import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { Subject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'lum-ui-autocomplete',
  templateUrl: './ui-autocomplete.component.html',
  styleUrls: ['./ui-autocomplete.component.scss'],
})
export class UiAutocompleteComponent implements OnInit, OnDestroy {
  @Input()
  inputClass :string = 'lum-input';

  @Input()
  placeholder :string = '';

  @Input()
  displayProperty :string = 'name'; // This is the property that will be displayed in the dropdown

  @Input()
  loading :boolean = false;

  @Input() loading$ :Observable<boolean>;

  @Input()
  debounceTime :number = 0;

  @Input()
  options :any[] | null = [];

  @Input()
  bindValue :string | null = null; // Return the property value of the selected option instead of the whole object

  @Input()
  value :string | null = null;

  @Input()
  displayNoResults :boolean = false;

  @Input()
  disabled :boolean = false;

  @Input()
  control :UntypedFormControl;

  @Output()
  inputChangeEvent = new EventEmitter<any>();

  @Output()
  inputFocusedEvent = new EventEmitter<any>();

  @Output()
  optionSelectedEvent = new EventEmitter<any>();

  @Output()
  scrollPaginate = new EventEmitter<boolean>();

  inputUpdated$ = new Subject<string>();
  scrollPaginateEnabled :boolean = false;

  private onDestroy$ = new Subject();

  constructor() {}

  ngOnInit() :void {
    this.onInputUpdate().subscribe();
    this.scrollPaginateEnabled = this.scrollPaginate.observers.length > 0;
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onInputChange(event :Event) :void {
    this.inputUpdated$.next((event.target as HTMLInputElement).value);
  }

  onSelectChange(event :MatAutocompleteSelectedEvent) :void {
    const value = this.bindValue
      ? event.option.value[this.bindValue]
      : event.option.value;

    this.optionSelectedEvent.emit(value);

    if (this.control) {
      this.control.setValue(value);
    }
  }

  onFocus(event :FocusEvent) :void {
    if (this.inputFocusedEvent.observers.length > 0) {
      this.inputFocusedEvent.emit();
    }
  }

  private onInputUpdate() :Observable<string> {
    return this.inputUpdated$.asObservable().pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      tap((value :string) => {
        this.inputChangeEvent.emit(value);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get displayFn() {
    return (option :any) :string => {
      return option && option[this.displayProperty]
        ? option[this.displayProperty]
        : '';
    };
  }

  get displayValue() :string {
    if (this.bindValue) {
      return this.control?.value ?? '';
    } else {
      return this.control?.value?.[this.displayProperty] ?? this.value ?? '';
    }
  }
}
